import React, { useState, useEffect } from 'react';
import { useAuth } from './auth';
import CardWrapper from './CardWrapper';
import { getBackendURL } from './utils';
import AdminDashboard from './AdminDashboard';
import './SweatTextDashboard.css';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import RemindersInfo from './steps/RemindersInfo';
import { LoginPage } from './LoginPage';
import { GetStartedReminderButton } from './steps/RemindersInfo';
import ActivityTimeline from './ActivityTimeline';
import AggregateSmsHistory from './AggregateSmsHistory';

const customTooltipStyle = {
  backgroundColor: '#333',
  color: 'white',
  padding: '10px',
  borderRadius: '5px',
  border: '1px solid #555',
};

const customAxisStyle = { fontSize: '12px', fill: 'white' };

// ---------- Small UI helpers ----------
const SectionCard = ({ title, subtitle, children, style, ariaLabel }) => (
  <div
    className="fogged-card"
    role="region"
    aria-label={ariaLabel || title}
    style={{
      borderRadius: 12,
      padding: '1rem',
      margin: '1rem 0',
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      maxWidth: '100%',
      boxSizing: 'border-box',
      ...style,
    }}
  >
    {(title || subtitle) && (
      <header style={{ marginBottom: '0.5rem' }}>
        {title && <h3 style={{ margin: 0 }}>{title}</h3>}
        {subtitle && <p style={{ margin: 0, opacity: 0.9 }}>{subtitle}</p>}
      </header>
    )}
    {children}
  </div>
);

// KPI with label first -> value -> helper text (stronger hierarchy)
const MetricCard = ({ label, value, helper, style }) => (
  <div
    className="fogged-card"
    role="status"
    aria-live="polite"
    style={{
      borderRadius: 12,
      padding: '1rem',
      margin: '1rem 0',
      color: 'white',
      display: 'flex',
      alignItems: 'baseline',
      gap: '0.75rem',
      position: 'relative',
      overflow: 'hidden',
      maxWidth: '100%',
      boxSizing: 'border-box',
      ...style,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, letterSpacing: 0.3, opacity: 0.95 }}>
        {label}
      </div>
      <div style={{ fontSize: '3rem', lineHeight: 1, fontWeight: 800 }}>
        {value}
      </div>
      {helper && (
        <div style={{ opacity: 0.9, marginTop: 4 }}>
          {helper}
        </div>
      )}
    </div>
  </div>
);

// Empty state block with optional actions
const EmptyState = ({ title, body, action }) => (
  <div style={{
    border: '1px dashed rgba(255,255,255,0.25)',
    borderRadius: 12,
    padding: '1rem',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    maxWidth: '100%',
    boxSizing: 'border-box'
  }}>
    <h4 style={{ marginTop: 0 }}>{title}</h4>
    <p style={{ marginTop: 8 }}>{body}</p>
    {action}
  </div>
);

// Custom Tooltip Component
const CustomTooltip = ({ payload, label, active }) => {
  if (active && payload && payload.length) {
    return (
      <div style={customTooltipStyle}>
        <p>{`${label || 'Unknown'} : ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

// ---------- Client detail: improved toggle/accordion ----------
const ToggleButton = ({ isOpen, onClick, label }) => (
  <button
    className="ptc-button"
    onClick={onClick}
    aria-expanded={isOpen}
    aria-controls={`client-details-${label}`}
    style={{
      width: '100%',
      textAlign: 'left',
      padding: '0.75rem 1rem',
      fontSize: '0.95rem',
      cursor: 'pointer',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: 8,
      background: isOpen ? 'rgba(170, 170, 170, 0.25)' : 'rgba(255,255,255,0.06)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      boxSizing: 'border-box'
    }}
  >
    <span style={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{label}</span>
    <span style={{ opacity: 0.9 }}>{isOpen ? '▾' : '▸'}</span>
  </button>
);

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '140px minmax(0, 1fr) auto', // label | value | action
  gap: '8px 12px',
  alignItems: 'center',
  marginBottom: '0.5rem',
};

const stackRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 8,
  alignItems: 'center',
  marginBottom: '0.5rem',
};

const valueTextStyle = {
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

// Responsive styles
const responsiveRowStyle = {
  display: 'grid',
  gridTemplateColumns: '140px minmax(0, 1fr) auto',
  gap: '8px 12px',
  alignItems: 'center',
  marginBottom: '0.5rem',
  boxSizing: 'border-box',
};

const responsiveStackRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '8px 0',
  alignItems: 'start',
  marginBottom: '0.5rem',
  boxSizing: 'border-box',
};

function useResponsiveRowStyle() {
  const [isStacked, setIsStacked] = useState(false);
  useEffect(() => {
    function handleResize() {
      setIsStacked(window.innerWidth < 600);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isStacked ? responsiveStackRowStyle : responsiveRowStyle;
}

const TrainerClientDetail = ({ client, onRemove, onUpdate, onInvite }) => {
  const [open, setOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(client.pre_user_name || '');
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState(client.pre_user_email || '');
  const [editingTimeslots, setEditingTimeslots] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);

  const weekdays = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];


  const client_was_recently_sent_an_invite = client.sent_email_date && (new Date(client.sent_email_date) > (new Date(Date.now() - 24*60*60*1000)));

  const disableInviteButton = !client.pre_user_email || client.consent_timestamp || client_was_recently_sent_an_invite || inviteLoading;

  const handleNameEdit = () => setEditingName(true);
  const handleNameSave = () => {
    setEditingName(false);
    if (nameValue !== client.pre_user_name) {
      onUpdate({ ...client, pre_user_name: nameValue });
    }
  };

  const handleEmailEdit = () => setEditingEmail(true);
  const handleEmailSave = () => {
    setEditingEmail(false);
    if (emailValue !== client.pre_user_email) {
      onUpdate({ ...client, pre_user_email: emailValue });
    }
  };

  const handleTimeslotEdit = (day) => {
    setEditingTimeslots({ ...editingTimeslots, [day]: true });
  };
  const handleTimeslotSave = (day, value) => {
    setEditingTimeslots({ ...editingTimeslots, [day]: false });
    if (client[`timeslot_${day.toLowerCase()}`] !== value) {
      onUpdate({ ...client, [`timeslot_${day.toLowerCase()}`]: value });
    }
  };

  const inviteClientWithEmail = async (client) => {
    if (!client.pre_user_name) {
      alert('Please enter a name for the client before saving and inviting.');
      return;
    }
    if (!client.pre_user_email) {
      alert('Please enter an email address before saving and inviting.');
      return;
    }

    try {
      await fetch(getBackendURL() + '/api/invite-client', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
        },
        body: JSON.stringify({ email: client.pre_user_email, name: client.pre_user_name })
      });

      alert('Invitation email sent successfully!');
    } catch (err) {
      console.error('Error inviting client:', err);
      alert('Failed to invite client.');
    }
  };

  const handleInvite = async () => {
    setInviteLoading(true);
    await onInvite(client);
    setInviteLoading(false);
  };

  if (!client) return null;
  const summary =
    `${client.pre_user_name || client.pre_user_email || 'Unknown'} • ${client.consent_timestamp ? '✔' : '⚠'}`;

  const rowLayout = useResponsiveRowStyle();

  return (
    <div style={{ margin: '0.5rem 0', position: 'relative' }}>
      <ToggleButton
        isOpen={open}
        onClick={() => setOpen(!open)}
        label={summary}
      />
      <button
        aria-label="Remove client"
        style={{ position: 'absolute', right: 50, top: 8, 
          background: '#f44336', color: 'white', 
          border: 'none', borderRadius: '50%', 
          width: 32, height: 32, fontSize: 20, 
          padding: '0.1rem',
          cursor: 'pointer', zIndex: 2 }}
        onClick={() => {
          if (window.confirm('Are you sure you want to remove this client?')) onRemove(client);
        }}
      >✖</button>
      {open && (
        <div
          id={`client-details-${client.pre_user_email}`}
          style={{
            background: 'rgba(40, 167, 69, 0.15)',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '0.5rem',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Nickname row */}
          <div style={rowLayout}>
            <strong>Nickname:</strong>
            {editingName ? (
              <>
                <input value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  style={{ marginLeft: 0, width: '100%', maxWidth: 420, minWidth: 0 }} />
                <button className="ptc-button" style={{ justifySelf: 'start' }} onClick={handleNameSave}>
                  Save
                </button>
              </>
            ) : (
              <>
                <span style={valueTextStyle}>{client.pre_user_name || '-'}</span>
                <button className="ptc-button" style={{ justifySelf: 'start' }} onClick={handleNameEdit}>Edit</button>
              </>
            )}
          </div>

          {/* Email row */}
          <div style={rowLayout}>
            <strong>Email:</strong>
            {editingEmail ? (
              <>
                <input value={emailValue}
                  onChange={e => setEmailValue(e.target.value)}
                  style={{ width: '100%', maxWidth: 420, minWidth: 0 }} />
                <button className="ptc-button" style={{ justifySelf: 'start' }} onClick={handleEmailSave}>
                  Save
                </button>
              </>
            ) : (
              <>
                <span style={valueTextStyle}>
                  {client.pre_user_email || '-'}
                </span>
                {/* Only show Edit if no email set */}
                {!emailValue && (
                  <button className="ptc-button" style={{ justifySelf: 'start' }} onClick={handleEmailEdit}>
                    Edit
                  </button>
                )}
              </>
            )}
          </div>

          {/* Email invite button */}


          {/* We can also check if the .sent_email_date (which is a timestamp string)
          
          is within the last day, to avoid spamming invites.
          
          */}
          {!client.consent_timestamp 
          && (
            <div style={stackRowStyle}>
              <button
                className="ptc-button"
                onClick={() => inviteClientWithEmail(client)}
                style={{
                  background: disableInviteButton ? '#888': '#4caf50',
                  cursor: disableInviteButton ? 'not-allowed' : 'pointer' ,
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                disabled={disableInviteButton}
              >
                Send Client Invite Email
                {/* If disableInviteButton, describe why. */}
                {disableInviteButton && (
                  <div style={{ fontSize: '0.85rem', 
                    fontStyle: 'italic',
                    fontWeight: 'normal',
                  opacity: 0.9, 
                  marginTop: 8, 
                  marginBottom: 8 }}>
                    (
                    {!client.pre_user_email && 'Please enter a valid email address to send an invite.'}
                    {client.consent_timestamp && 'Client has already accepted the invite.'}
                    {client_was_recently_sent_an_invite && 'Invite was sent recently. Please wait 24 hours before sending another.'}
                    {inviteLoading && 'Sending invite...'}
                    )
                  </div>
                )}
              </button>
            </div>
          )}


          {/* Status rows */}
          <div style={rowStyle}>
            <strong>Invite Status:</strong>
            <span style={valueTextStyle}>{client.consent_timestamp ? 'Accepted' : 'Not accepted'}</span>
            <span />
          </div>
          <div style={rowStyle}>
            <strong>Phone:</strong>
            <span style={valueTextStyle}>{client?.user?.phone || '-'}</span>
            <span />
          </div>
          
          
          {/* Maybe leave this out for now... test specifically because 
          i would not want to be wrong and i am not even sure the 
          trainer would appreciate this detail - might be noise.  */}
          {/* <div style={rowStyle}>
            <strong>Notification Opt-in:</strong>
            <span style={valueTextStyle}>{client?.user?.settings?.sms_opt_in ? 'Yes' : 'No'}</span>
            <span />
          </div> */}

          {/* Weekly schedule */}
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>Appointment Timeslots:</strong>
            <div
              className="weekly-schedule"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
                marginTop: 8,
              }}
            >
              {weekdays.map(day => (
                <div key={day} className="day" style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto', // name | value | button
                  alignItems: 'center',
                  gap: 8,
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  padding: 10,
                  minWidth: 0,
                }}>
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{day}</span>
                  {editingTimeslots[day] ? (
                    <>
                      <input
                        type="time"
                        value={client[`timeslot_${day.toLowerCase()}`] || ''}
                        onChange={e => handleTimeslotSave(day, e.target.value)}
                        style={{ marginLeft: 0, minWidth: 0 }}
                      />
                      <button className="ptc-button" onClick={() => handleTimeslotSave(day, client[`timeslot_${day.toLowerCase()}`])}>Save</button>
                    </>
                  ) : (
                    <>
                      <div className="timeslot" style={{
                        justifySelf: 'end',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {client[`timeslot_${day.toLowerCase()}`] || '-'}
                      </div>
                      <button className="ptc-button" onClick={() => handleTimeslotEdit(day)}>Edit</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Main ----------
const SweatTextDashboard = () => {
  const { user, accessToken, fetchUserInfo } = useAuth();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [trainerClients, setTrainerClients] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);

  const [smsHistory, setSmsHistory] = useState([]);
  const [smsLoading, setSmsLoading] = useState(true);

  const [trainerSmsHistory, setTrainerSmsHistory] = useState([]);
  const [trainerSmsLoading, setTrainerSmsLoading] = useState(true);

  const [trainersTrainerInfo, setTrainersTrainerInfo] = useState(null);
  const [clientsTrainerInfo, setClientsTrainerInfo] = useState(null);

  // const [settings, setSettings] = useState(user?.settings || null);

  // for charts
  const [smsActivityData, setSmsActivityData] = useState(null);
  const [activityTimeline, setActivityTimeline] = useState([]);

  const [nextAppointment, setNextAppointment] = useState(null);

  const sectionStyles = {
    trainerInfo: { backgroundColor: 'rgba(0, 123, 255, 0.1)' },
    clientInfo: { backgroundColor: 'rgba(40, 167, 69, 0.1)' },
    smsHistory: { backgroundColor: 'rgba(255, 193, 7, 0.1)' },
    smsActivity: { backgroundColor: 'rgba(0, 200, 83, 0.1)' },
  };

  // ------- Data fetch hooks (unchanged logic) -------
  useEffect(() => {
    if (!user) return;
    setLoading(true);

    if (user.trainer_info) {
      fetch(getBackendURL() + `/client?trainer_info_id=${user.trainer_info.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      })
        .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch trainer clients'))
        .then((clients) => setTrainerClients(clients || []))
        .catch((err) => console.error('Error fetching trainer clients:', err))
        .finally(() => setLoading(false));
    } else if (user.client) {
      fetch(getBackendURL() + `/client/${user.client.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      })
        .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch client info'))
        .then((info) => setClientInfo(info))
        .catch((err) => console.error('Error fetching client info:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user, accessToken]);


  // This is probably the part where I ensured the 
  // settings were made for every user - however
  // I later switched to a strategy where 
  // getting the user from anywhere already 
  // will create a settings if none exist.
  // So this is not necessary and just by 
  // accessing user via useAuth() we
  // should have settings.
  // Any updates to the user settings shsould just be followed by a 
  // call to fetchUserInfo() to refresh the user object.
  // Then of course we want to avoid using a separate settings.
  // useEffect(() => {
  //   if (!settings && user) {
  //     fetch(getBackendURL() + `/user_settings/${user.id}/?user_id=${user.id}`, {
  //       method: 'GET',
  //       headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
  //     })
  //       .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch user settings'))
  //       .then((data) => setSettings(data))
  //       .catch((err) => console.error('Error fetching user settings:', err));
  //   }
  // }, [settings, user, accessToken]);

  const handleToggleSmsOptIn = async (checked) => {
    // if (!settings) return;
    try {
      const res = await fetch(getBackendURL() + `/user_settings/${user?.settings?.id}?user_id=${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ sms_opt_in: checked }),
      });
      if (res.ok) {
        const updatedSettings = await res.json();
        // setSettings(updatedSettings);
        fetchUserInfo(); // Refresh user info to get updated settings
        // not awaiting because the result is not needed here
      }
    } catch (err) {
      console.error('Error updating SMS opt-in setting:', err);
    }
  };

  // Client SMS history
  useEffect(() => {
    if (!user || user.role.includes('trainer')) return;
    if (!user?.id) return;

    setSmsLoading(true);
    fetch(getBackendURL() + `/sms_message?recipient_id=${encodeURIComponent(user?.id || '')}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch SMS history');
        return res.json();
      })
      .then((data) => setSmsHistory(data || []))
      .catch((err) => console.error('Error fetching SMS history:', err))
      .finally(() => setSmsLoading(false));
  }, [user, accessToken]);

  // Trainer aggregate SMS
  useEffect(() => {
    if (!user || !user.role.includes('trainer')) return;
    setTrainerSmsLoading(true);
    fetch(getBackendURL() + `/sms_message?sender_id=${encodeURIComponent(user?.trainer_info?.user.id || '')}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch trainer SMS history');
        return res.json();
      })
      .then((data) => setTrainerSmsHistory(data || []))
      .catch((err) => console.error('Error fetching trainer SMS history:', err))
      .finally(() => setTrainerSmsLoading(false));
  }, [user, accessToken]);

  // Trainer info for clients
  useEffect(() => {
    if (!user || user.role.includes('trainer')) return;
    const trainerId = user?.client?.trainer_info_id;
    if (!trainerId) return;

    fetch(getBackendURL() + `/trainer_info/${encodeURIComponent(trainerId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch trainer info');
        return res.json();
      })
      .then((data) => setClientsTrainerInfo(data))
      .catch((err) => console.error('Error fetching trainer info:', err));
  }, [user, accessToken]);

  // Trainer info for trainers
  useEffect(() => {
    if (!user || !user.role.includes('trainer')) return;
    const trainerId = user?.trainer_info?.id;
    if (!trainerId) return;

    fetch(getBackendURL() + `/trainer_info/${encodeURIComponent(trainerId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch trainer info');
        return res.json();
      })
      .then((data) => setTrainersTrainerInfo(data))
      .catch((err) => console.error('Error fetching trainer info:', err));
  }, [user, accessToken]);

  // Prep data: line chart & timeline
  useEffect(() => {
    const groupedByDate = trainerSmsHistory.reduce((acc, sms) => {
      const date = new Date(sms.sent_at).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(groupedByDate);
    const values = Object.values(groupedByDate);

    const safeLabels = labels.length ? labels : ['No activity yet'];
    const safeValues = values.length ? values : [0];

    setSmsActivityData({
      labels: safeLabels,
      datasets: [{ label: 'SMS Activity', data: safeValues }],
    });

    const timeline = [...(smsHistory || []), ...(trainerSmsHistory || [])]
      .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
    setActivityTimeline(timeline.slice(0, 10));
  }, [smsHistory, trainerSmsHistory]);

  // Utility to display local time
  function formatLocalDateTime(sentAtStr) {
    if (sentAtStr && !sentAtStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(sentAtStr)) {
      sentAtStr += 'Z';
    }
    return sentAtStr ? new Date(sentAtStr).toLocaleString() : '-';
  }

  const handleAddClient = async () => {
    try {
      const res = await fetch(getBackendURL() + '/client', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ trainer_info_id: user.trainer_info.id })
      });
      if (!res.ok) throw new Error('Failed to add client');
      const newClient = await res.json();
      setTrainerClients([...trainerClients, newClient]);
    } catch (err) {
      alert('Failed to add client.');
    }
  };

  const handleRemoveClient = async (client) => {
    if (!client || !client.id) return;
    try {
      const res = await fetch(getBackendURL() + `/client/${client.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
      });
      if (!res.ok) throw new Error('Failed to remove client');
      setTrainerClients(trainerClients.filter(c => c.id !== client.id));
    } catch (err) {
      alert('Failed to remove client.');
    }
  };

  const handleUpdateClient = async (updatedClient) => {
    if (!updatedClient || !updatedClient.id) return;
    try {
      const res = await fetch(getBackendURL() + `/client/${updatedClient.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(updatedClient)
      });
      if (!res.ok) throw new Error('Failed to update client');
      const newClient = await res.json();
      setTrainerClients(trainerClients.map(c => c.id === newClient.id ? newClient : c));
    } catch (err) {
      alert('Failed to update client.');
    }
  };

  const handleInviteClient = async (client) => {
    if (!client || !client.pre_user_email || !client.pre_user_name) return;
    try {
      const res = await fetch(getBackendURL() + '/api/invite-client', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          email: client.pre_user_email,
          name: client.pre_user_name
        })
      });
      if (!res.ok) throw new Error('Failed to send invite');
    } catch (err) {
      alert('Failed to send invite email.');
    }
  };

  useEffect(() => {
    if (!user || !user.role.includes('trainer')) return;
    fetch(getBackendURL() + '/api/next-appointment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch next appointment'))
      .then(data => setNextAppointment(data))
      .catch(err => {
        setNextAppointment(null);
        console.error('Error fetching next appointment:', err);
      });
  }, [user, accessToken]);

  const [smsReminderLoading, setSmsReminderLoading] = useState(false);

  const handleToggleSmsReminder = async () => {
    if (!user?.trainer_info) return;
    setSmsReminderLoading(true);
    try {
      const res = await fetch(getBackendURL() + '/trainer_info/' + user.trainer_info.id, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ sms_reminder_feature_enabled: !user.trainer_info.sms_reminder_feature_enabled }),
      });
      if (!res.ok) throw new Error('Failed to update SMS reminder feature');
      const updatedTrainerInfo = await res.json();
      // Update user.trainer_info locally
      user.trainer_info.sms_reminder_feature_enabled = updatedTrainerInfo.sms_reminder_feature_enabled;
    } catch (err) {
      alert('Failed to update SMS reminder feature.');
    }
    setSmsReminderLoading(false);
  };

  if (!user) {
    // Refresh page to trigger auth flow
    window.location.reload();
    return (
      <CardWrapper>
        <h2 className="ptc-header">SweatText Dashboard</h2>
        <p>Redirecting to login...</p>
      </CardWrapper>
    );
  }

  if (loading) {
    return (
      <CardWrapper>
        <h2 className="ptc-header">SweatText Dashboard</h2>
        <p>Loading your dashboard...</p>
      </CardWrapper>
    );
  }


  // if user has role including trainer but no trainer_info, redirect to /tools/reminders/step1
  if (user.role.includes('trainer') && !user.trainer_info) {
    // navigate('/');
    // return (
    //   <CardWrapper>
    //     <h2 className="ptc-header">SweatText Trainer Dashboard</h2>
    //     <p>You need to set up your trainer profile before accessing the dashboard.</p>
    //     Please refresh the page - if the problem persists, contact support.
    //   </CardWrapper>
    // )

    // The trainer info is being handled and we just want to show a spinner 
    // and then after 1 second redirect. But dont redirect more than once so use a param 
    // to indicate we already did the redirect.
    if (!window.location.search.includes('redirected=true')) {
      setTimeout(async () => {
        await fetchUserInfo(); // Ensure user info is fresh
        // navigate('/?redirected=true');
      }, 3000);

      return (
        <CardWrapper>
          <h2 className="ptc-header">SweatText Trainer Dashboard</h2>
          {/* Show a spinner  */}
          <p>Loading your trainer profile. If this takes too long, please refresh the page.</p>
          <div style={{ fontSize: '2.5rem', verticalAlign: 'middle' }}>
            <span className="spinner" style={{ display: 'inline-block', width: 32, height: 32, border: '4px solid #4caf50', borderRadius: '50%', borderTop: '4px solid #fff', animation: 'spin 1s linear infinite' }} />
          </div>
        </CardWrapper>
      );
    }
    return (
      <CardWrapper>
        <h2 className="ptc-header">SweatText Trainer Dashboard</h2>
        <p>Loading your dashboard, but it is taking longer than expected. Please refresh in 10 seconds.</p>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: 'white' }}>
          {user.role.includes('trainer')
            ? 'SweatText Trainer Dashboard'
            : user.role.includes('admin')
              ? 'SweatText Admin Dashboard'
              : 'SweatText Client Dashboard'}
        </h2>
      </div>

      {user && !user.role.includes('client') && (
        <div><RemindersInfo /></div>
      )}

      {user.role.includes('trainer') ? (
        <>
          <SectionCard style={sectionStyles.trainerInfo} title={`Welcome back, ${user.name}!`} />

          <div onClick={handleToggleSmsReminder} style={{ cursor: smsReminderLoading ? 'wait' : 'pointer' }}>
            <MetricCard
              label="SMS Reminder System"
              value={(smsReminderLoading || !user) ? (
                <span style={{ fontSize: '2.5rem', verticalAlign: 'middle' }}>
                  <span className="spinner" style={{ display: 'inline-block', width: 32, height: 32, border: '4px solid #4caf50', borderRadius: '50%', borderTop: '4px solid #fff', animation: 'spin 1s linear infinite' }} />
                </span>
              ) : user?.trainer_info?.sms_reminder_feature_enabled ? (
                <span style={{ color: '#4caf50', fontSize: '2.5rem', verticalAlign: 'middle' }}>✔</span>
              ) : (
                <span style={{ color: '#ff9800', fontSize: '2.5rem', verticalAlign: 'middle' }}>⏸</span>
              )}
              helper={user?.trainer_info?.sms_reminder_feature_enabled ? 'Active (click to pause)' : 'Paused (click to activate)'}
              style={sectionStyles.trainerInfo}
            />
          </div>

          <MetricCard
            label="Next Appointment"
            value={nextAppointment && nextAppointment.appointment ? nextAppointment.appointment : 'None'}
            helper={nextAppointment && nextAppointment.client && nextAppointment.client.name ? `with ${nextAppointment.client.name}` : ''}
            style={sectionStyles.trainerInfo}
          />
          {/* <MetricCard
            label="Clients Accepted"
            helper="(Sorry, this feature is not ready yet)"
            style={sectionStyles.trainerInfo}
          />

          <MetricCard
            label="Reminders Sent"
            helper="(Sorry, this feature is not ready yet)"
            style={sectionStyles.trainerInfo}
          /> */}

          {/* Clients list */}
          <SectionCard
            style={sectionStyles.clientInfo}
            title="Your Clients"
            subtitle="Click a row to view details"
            ariaLabel="Your Clients' Information"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {trainerClients.length > 0 ? (
                trainerClients.map((client) => (
                  <TrainerClientDetail
                    key={client.id || client.pre_user_email}
                    client={client}
                    onRemove={handleRemoveClient}
                    onUpdate={handleUpdateClient}
                    onInvite={handleInviteClient}
                  />
                ))
              ) : (
                <EmptyState title="No clients yet" body="" action={<></>} />
              )}
            </div>
            <button
              className="ptc-button"
              style={{ background: '#4caf50', color: 'white', width: '100%', marginTop: 16, fontSize: 20, padding: '0.75rem 0', boxSizing: 'border-box' }}
              onClick={handleAddClient}
            >
              + Add New Client
            </button>
          </SectionCard>

          {/* Line Chart — always render (zero-data friendly) */}
          <SectionCard style={sectionStyles.smsActivity} title="Reminders Sent Over Time">
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={smsActivityData?.labels.map((label, i) => ({
                    date: label,
                    count: smsActivityData?.datasets[0]?.data[i],
                  })) || []}
                >
                  <XAxis dataKey="date" tick={{ ...customAxisStyle }} />
                  <YAxis tick={{ ...customAxisStyle }} />
                  <Tooltip content={<CustomTooltip />} />
                  <CartesianGrid stroke="#555" strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="count" stroke="#FF6384" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {trainerSmsHistory.length === 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <EmptyState
                  title="No SMS activity yet"
                  body="Messages will show up here after your first appointment approaches."
                  action={<></>}
                />
              </div>
            )}
          </SectionCard>

          {/* Existing aggregates & timeline */}
          <AggregateSmsHistory
            trainerSmsHistory={trainerSmsHistory}
            trainerSmsLoading={trainerSmsLoading}
            formatLocalDateTime={formatLocalDateTime}
          />

          <ActivityTimeline
            activityTimeline={activityTimeline}
            formatLocalDateTime={formatLocalDateTime}
          />
        </>
      ) : (
        <>
          {/* Client-side info cards kept; wrapped with SectionCard for consistency */}
          <SectionCard
            title="Personal Information"
            style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)' }}
          >
            <p><strong>Name:</strong> {user?.name || '-'}</p>
            <p style={valueTextStyle}><strong>Email:</strong> {user?.email || '-'}</p>
            <p style={valueTextStyle}><strong>Email Used to Invite You:</strong> {user?.client?.pre_user_email || '-'}</p>
            <p style={valueTextStyle}><strong>Phone:</strong> {user?.phone || '-'}</p>
            <p><strong>Invite Status:</strong> {clientInfo?.consent_timestamp ? 'Accepted' : 'Not accepted yet'}</p>
          </SectionCard>

          <SectionCard
            title="Trainer Information"
            style={{ backgroundColor: 'rgba(0, 123, 255, 0.1)' }}
          >
            <p><strong>Your Trainer's Trainer ID:</strong> {user?.client?.trainer_info_id || '-'}</p>
            <p><strong>Your Trainer's Name:</strong> {clientsTrainerInfo?.user?.name || '-'}</p>
            <p><strong>Is your trainer's SMS reminder system currently activated?</strong> {clientsTrainerInfo?.sms_reminder_feature_enabled ? 'Yes' : 'No'}</p>
            <p style={{ color: 'white' }}>
              If you'd like to change or remove your trainer from your client profile, please{' '}
              <a href="/" style={{ color: '#4aa3ff' }}>contact us</a>.
            </p>
          </SectionCard>

          <SectionCard
            title="Appointment Timeslots"
            style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}
          >
            <div className="weekly-schedule" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Sunday</span><div className="timeslot">{clientInfo?.timeslot_sunday || '-'}</div>
              </div>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Monday</span><div className="timeslot">{clientInfo?.timeslot_monday || '-'}</div>
              </div>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Tuesday</span><div className="timeslot">{clientInfo?.timeslot_tuesday || '-'}</div>
              </div>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Wednesday</span><div className="timeslot">{clientInfo?.timeslot_wednesday || '-'}</div>
              </div>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Thursday</span><div className="timeslot">{clientInfo?.timeslot_thursday || '-'}</div>
              </div>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Friday</span><div className="timeslot">{clientInfo?.timeslot_friday || '-'}</div>
              </div>
              <div className="day" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <span>Saturday</span><div className="timeslot">{clientInfo?.timeslot_saturday || '-'}</div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="SMS Reminder Settings"
            style={{ backgroundColor: 'rgba(102, 16, 242, 0.1)' }}
          >
            <label>
              <input
                type="checkbox"
                checked={user?.settings?.sms_opt_in || false}
                onChange={(e) => handleToggleSmsOptIn(e.target.checked)}
              />
              {' '}Receive SMS reminders
            </label>
          </SectionCard>

          <SectionCard style={sectionStyles.smsHistory} title={`SMS History${smsHistory?.length ? ` (${smsHistory.length})` : ''}`}>
            <div
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '0.5rem',
              }}
            >
              {smsLoading ? (
                <p>Loading...</p>
              ) : smsHistory.length === 0 ? (
                <EmptyState
                  title="No messages yet"
                  body="When your trainer sends you a reminder, it will show up here."
                />
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {smsHistory.map((sms, index) => (
                    <li key={index} style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                      <p><b>From:</b> {sms.sender_id} {sms.sender_id && '/'}{!sms.sender_id && 'Unknown'} {sms.sender?.user?.name}</p>
                      <p><b>To:</b> {sms.recipient_phone_number}</p>
                      <p><b>Message:</b> {sms.content}</p>
                      <p><b>Sent At:</b> {formatLocalDateTime(sms.sent_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </SectionCard>
        </>
      )}

      {user.role.includes('admin') && (
        <AdminDashboard accessToken={accessToken} />
      )}
    </CardWrapper>
  );
};

export default SweatTextDashboard;
